import tinycolor from "tinycolor2";
import { Components } from "@mui/material";
import { A2PTheme, ColorPalette } from "./theme";
const primary = "#BF0000";
const secondary = "#000000";
const bright = "#FAFAFF";
const warning = "#FF8F1F";
const success = "#6EB874";
const info = "#CACADB";
const NOTIFICATION_COLORS = {
  alert: "#DF0101",
  success: "#047205",
  info: "#006497",
  warning: "#7D6E00",
  danger: "#DF0101",
};
export const UI: ColorPalette = {
  dark_blue_5: "#e6eeff",
  black_300: "#B6B6B6",
  dark_blue_10: "#E6E6F0",
  dark_green: "#10BD10",
  blue_20: "#CECEF5",
  blue_40: "#9999E0",
  blue_80: "#606060",
  blue_90: "#E1E1E1",
  blue_100: "#E8EEFF",
  blue_200: "#90C6E0",
  blue_1000: "#006497",
  purple: "#9B00EC",
  purple_900: "#4B0071",
  purple_800: "#65009A",
  purple_700: "#7D00BE",
  purple_600: "#9B00EC",
  purple_500: "#B62AFF",
  purple_5: "#F8F2FF",
  purple_10: "#F7E7FF",
  purple_20: "#E3CCFF",
  purple_40: "#C799FF",
  purple_100: "#5C00CC",
  pink: "#EF0AA1",
  pink_80: "#CC0070",
  pink_100: "#FF008C",
  white: "#FFFFFF",
  black: "#000000",
  black_700: "#4D4D4D",
  light_black: "#676767",
  gray: "#F7F7F7",
  grey: "#F2F2F2",
  grey_100: "#EBEBEB",
  grey_300: "#D1D1D1",
  grey_500: "#9C9C9C",
  grey_700: "#686868",
  grey_800: "#333333",
  grey_900: "#000000",
  grey_91: "#E8E8E8",
  grey_98: "#FAFAFA",
  green_1000: "#00B900",
  warn: "#705A00",
  rose: "#828282",
  light_rose: "#FC7092",
  dark_rose: "#90003B",
  secondary_rose: "#C53E6C",
  secondary_hover_rose: "#A53159",
  crimson_red: "#BF0000",
  rose_200: "#FC7092",
  background: "#FFF0F0",
  text_grey: "#DADADA",
  text_grey_700: "#898989",
  red: "#E61717",
  red_50: "#FFE8E8",
  red_800: "#8C0000",
  orange: "#F59600",
  light_red: "#FF5959",
  navy: "#0019B9",
  disabled: "#DFDFDF",
  disabled_text: "#898989",
  grey_1000: "#F1F3F4",
  aqua: "#00B4E5",
  deep_red: "#8C0000",
  gold: "#F5E300"
};
const GRAPH = {
  blue: "#3B3BA3",
  orange: "#F59600",
  purple: "#C799FF",
  grey: "#9C9C9C",
  // QoS Dots, // including QoS panel
  success: "#10BD10",
  danger: "#DF0101",
  green: "#10BD10",
  red: "#E94D4D",
  defaultLinear: "#eaeaea",
  yellow: "#FFB238",
};
const lightenRate = 7.5;
const darkenRate = 15;
const theme: A2PTheme = {
  palette: {
    header: {
      default: primary,
    },
    background: {
      default: UI.grey,
      bright: "#FFE6F4",
      light: "#E8EEFF",
      success: "#EBF7EC",
      danger: "#FFF0F0",
      error: "#FFF0F0",
      warn: "#FFF5CC",
      warning: "#FEF0DD",
      info: "#EBF7FE",
      alert: "#DF0101",
    },
    analytics: {
      volume: "#EBF7FE",
      revenue: "#EBF7EC",
      cost: "#FFF5CC",
      margin: "#FFF0F0",
    },
    text: {
      link: "#007ACC",
      link_focused: "#009BF5",
      link_active: "#146AA3",
      black: "#333333",
      primary: "#333333",
      secondary: "#6E6E6E",
      hint: "#B9B9B9",
      pageTitle: "#001e6d",
      success: "#047205",
      warning: "#DF0101",
      danger: "#DF0101",
      info: "#000033",
      alert: "#990000",
      label: "#333333",
      text: "#DF0101",
      MediumGray: "#B3B3B3",
      placeholder_text: UI.grey_800,
      input: UI.grey_800,
    },
    NOTIFICATION_COLORS: NOTIFICATION_COLORS,
    UI: UI,
    GRAPH: GRAPH,
    primary: {
      main: UI.purple, //changed (main ctive color input)
      light: tinycolor(primary).lighten(lightenRate).toHexString(),
      dark: tinycolor(primary).darken(darkenRate).toHexString(),
    },
    secondary: {
      main: secondary,
      light: tinycolor(secondary).lighten(lightenRate).toHexString(),
      dark: tinycolor(secondary).darken(darkenRate).toHexString(),
      contrastText: "#FFFFFF",
    },
    bright: {
      main: bright,
      light: tinycolor(bright).lighten(lightenRate).toHexString(),
      dark: tinycolor(bright).darken(darkenRate).toHexString(),
    },
    warning: {
      main: warning,
      light: tinycolor(warning).lighten(lightenRate).toHexString(),
      dark: tinycolor(warning).darken(darkenRate).toHexString(),
    },
    success: {
      main: success,
      light: tinycolor(success).lighten(lightenRate).toHexString(),
      dark: tinycolor(success).darken(darkenRate).toHexString(),
    },
    info: {
      main: info,
      light: tinycolor(info).lighten(lightenRate).toHexString(),
      dark: tinycolor(info).darken(darkenRate).toHexString(),
    },
    purple: {
      main: UI.purple_40,
      light: tinycolor(UI.purple_40).lighten(lightenRate).toHexString(),
      dark: tinycolor(UI.purple_40).darken(darkenRate).toHexString(),
    },
    blue: {
      main: UI.blue_80,
      light: tinycolor(UI.blue_80).lighten(lightenRate).toHexString(),
      dark: tinycolor(UI.blue_80).darken(darkenRate).toHexString(),
    },
    pink: {
      main: UI.pink,
    },
  },
  button: {
    contained: {
      background: {
        primary: UI.black,
        secondary: "",
        hover: UI.black_700,
        active: UI.red_800,
        enabled: UI.green_1000,
        suspended: UI.red,
        pending: UI.orange,
        retired: UI.grey_500,
        focus: UI.black,
        scheduled: UI.aqua,
        cancelled: UI.red,
        unsuccessful: UI.aqua,
        failed: UI.deep_red,
        dropped: UI.grey,
        expired: UI.gold,
        partial: UI.green_1000
      },
      color: {
        primary: UI.white,
        secondary: UI.black,
      },
    },
    outlined: {
      background: {
        primary: UI.white,
        secondary: "",
        hover: UI.black_700,
        disable: UI.grey_91,
      },
      color: {
        primary: UI.black,
        secondary: "",
        hover: UI.white,
        disable: UI.grey_500,
      },
    },
  },
  dropdown: {
    text: UI.grey_900,
    selected: {
      color: UI.purple,
      backgroundColor: UI.gray,
    },
    unselected: {
      color: UI.blue_80,
      hover: {
        backgroundColor: UI.black_700,
        color: UI.white,
      },
    },
  },
  chip: {
    text: UI.black,
    backgroundColor: UI.secondary_rose,
    active: {
      backgroundColor: UI.secondary_rose,
    },
  },
  input: {
    label: UI.grey_800,
  },
  switch: {
    backgroundColor: UI.black,
    active: {
      backgroundColor: UI.purple,
    },
  },
  calendar: {
    borderColor: UI.grey_800,
  },
  link: {
    color: UI.purple_700,
    active: UI.purple_700,
    hover: UI.black_700,
  },
  template: {
    borderColor: UI.grey_300,
    hover: UI.black_700,
    active: UI.grey_800,
    breadcrumbColor: UI.black,
  },
  stepper: {
    active: UI.purple_700,
    completed: UI.black,
  },
  tab: {
    color: UI.grey_700,
    active: UI.black,
  },
};

export const symphonyMuiOverrides: Components = {
  MuiLink: {
    styleOverrides: {
      root: {
        textDecoration: "none",
        color: UI.purple_700,
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      root: {
        color: UI.purple_700,
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: "42px !important",
      },
    },
  },
};

export const symphonyTheme = {
  palette: {
    text: {
      primary: "#333333",
      success: "#047205",
    },
  },
};
export default theme;
